import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronRight,
  FiInfo,
  FiRefreshCw,
} from 'react-icons/fi';
import { apiJson } from '@/lib/api';
import type {
  AttributionCampaignRow,
  AttributionCampaignsResponse,
  AttributionDayPoint,
  AttributionGadsFunnel,
  AttributionMonitorResponse,
  CampaignGroup,
  DataQualityCheck,
  DataQualitySeverity,
  MatchRatePoint,
} from '@/lib/types';

type SubTab = 'main' | 'campaigns';
const ALLOWED_TABS = new Set<string>(['main', 'campaigns']);

const RANGE_OPTIONS = [
  { days: '7', label: '7d' },
  { days: '14', label: '14d' },
  { days: '30', label: '30d' },
  { days: '90', label: '90d' },
] as const;

type RangeDays = (typeof RANGE_OPTIONS)[number]['days'];
const ALLOWED_DAYS = new Set<string>(RANGE_OPTIONS.map((r) => r.days));

function pct(n: number): string {
  return `${n.toFixed(1)}%`;
}

function StatCard({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'good' | 'warn' | 'alert';
}) {
  const toneCls =
    tone === 'alert'
      ? 'border-rose-300 bg-rose-50'
      : tone === 'warn'
        ? 'border-amber-300 bg-amber-50'
        : tone === 'good'
          ? 'border-emerald-200 bg-emerald-50'
          : 'border-gray-200 bg-white';
  return (
    <div className={`rounded-lg shadow-sm border p-4 ${toneCls}`}>
      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
        {label}
      </p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
        {value}
      </p>
      {hint ? <p className='mt-1 text-xs text-gray-500'>{hint}</p> : null}
    </div>
  );
}

const SEVERITY_STYLE: Record<
  DataQualitySeverity,
  { box: string; icon: JSX.Element; chip: string }
> = {
  alert: {
    box: 'border-rose-200 bg-rose-50',
    icon: <FiAlertTriangle className='h-4 w-4 text-rose-600' aria-hidden />,
    chip: 'bg-rose-100 text-rose-800',
  },
  warn: {
    box: 'border-amber-200 bg-amber-50',
    icon: <FiAlertTriangle className='h-4 w-4 text-amber-600' aria-hidden />,
    chip: 'bg-amber-100 text-amber-900',
  },
  ok: {
    box: 'border-emerald-200 bg-emerald-50',
    icon: <FiCheckCircle className='h-4 w-4 text-emerald-600' aria-hidden />,
    chip: 'bg-emerald-100 text-emerald-800',
  },
  info: {
    box: 'border-gray-200 bg-gray-50',
    icon: <FiInfo className='h-4 w-4 text-gray-500' aria-hidden />,
    chip: 'bg-gray-100 text-gray-700',
  },
};

function DataQualityPanel({ checks }: { checks: DataQualityCheck[] }) {
  return (
    <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
      {checks.map((c) => {
        const s = SEVERITY_STYLE[c.severity];
        return (
          <div
            key={c.key}
            className={`flex items-start gap-3 rounded-lg border p-3 ${s.box}`}
          >
            <div className='mt-0.5 shrink-0'>{s.icon}</div>
            <div className='min-w-0 flex-1'>
              <div className='flex items-center justify-between gap-2'>
                <p className='text-sm font-medium text-gray-900'>{c.label}</p>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${s.chip}`}
                >
                  {c.count.toLocaleString()}
                </span>
              </div>
              <p className='mt-1 text-xs text-gray-600'>{c.description}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function VolumeChart({ data }: { data: AttributionDayPoint[] }) {
  if (!data.length) {
    return <p className='text-sm text-gray-500'>No attributions in this range.</p>;
  }
  const max = Math.max(1, ...data.map((d) => d.total));
  return (
    <div className='space-y-1'>
      <div className='flex items-center gap-4 text-xs text-gray-500 mb-2'>
        <span className='inline-flex items-center gap-1'>
          <span className='h-2 w-2 rounded-sm bg-sky-500' /> iOS (web)
        </span>
        <span className='inline-flex items-center gap-1'>
          <span className='h-2 w-2 rounded-sm bg-violet-500' /> Android
        </span>
      </div>
      <div className='flex items-end gap-[2px] h-32'>
        {data.map((d) => {
          const h = (d.total / max) * 100;
          const iosFrac = d.total > 0 ? d.ios / d.total : 0;
          return (
            <div
              key={d.date}
              className='group relative flex-1 min-w-[3px] flex flex-col justify-end'
              style={{ height: '100%' }}
              title={`${d.date} · ${d.total} total (${d.ios} iOS, ${d.android} Android)`}
            >
              <div
                className='w-full rounded-t-sm overflow-hidden flex flex-col'
                style={{ height: `${h}%` }}
              >
                <div
                  className='bg-sky-500 w-full'
                  style={{ height: `${iosFrac * 100}%` }}
                />
                <div
                  className='bg-violet-500 w-full'
                  style={{ height: `${(1 - iosFrac) * 100}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className='flex justify-between text-[10px] text-gray-400 pt-1'>
        <span>{data[0]?.date}</span>
        <span>{data[data.length - 1]?.date}</span>
      </div>
    </div>
  );
}

function MatchRateTrend({ data }: { data: MatchRatePoint[] }) {
  if (!data.length) {
    return <p className='text-sm text-gray-500'>No fingerprint data.</p>;
  }
  const maxPct = Math.max(1, ...data.map((d) => d.match_pct));
  return (
    <div className='space-y-2'>
      {data.map((d) => (
        <div key={d.period} className='flex items-center gap-3'>
          <span className='w-16 shrink-0 text-xs font-mono text-gray-600'>
            {d.period}
            {d.is_partial ? '*' : ''}
          </span>
          <div className='flex-1 h-5 rounded bg-gray-100 overflow-hidden'>
            <div
              className='h-full bg-sky-500 rounded'
              style={{ width: `${(d.match_pct / maxPct) * 100}%` }}
            />
          </div>
          <span className='w-44 shrink-0 text-right text-xs tabular-nums text-gray-700'>
            {pct(d.match_pct)} ·{' '}
            <span className='text-gray-500'>
              {d.matched.toLocaleString()}/{d.captured.toLocaleString()}
            </span>
          </span>
        </div>
      ))}
      <p className='text-[11px] text-gray-400'>
        * current month is partial — matches lag capture, so the rate rises as
        recent visitors sign up.
      </p>
    </div>
  );
}

function FunnelBar({
  label,
  value,
  max,
  color,
  sub,
}: {
  label: string;
  value: number;
  max: number;
  color: string;
  sub?: string;
}) {
  const w = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className='flex items-center gap-3'>
      <span className='w-40 shrink-0 text-xs text-gray-600'>{label}</span>
      <div className='flex-1 h-6 rounded bg-gray-100 overflow-hidden'>
        <div
          className='h-full rounded flex items-center justify-end pr-2'
          style={{ width: `${Math.max(w, 6)}%`, backgroundColor: color }}
        >
          <span className='text-[11px] font-semibold text-white tabular-nums'>
            {value.toLocaleString()}
          </span>
        </div>
      </div>
      <span className='w-12 shrink-0 text-right text-[11px] text-gray-400'>
        {sub ?? ''}
      </span>
    </div>
  );
}

function GadsFunnel({ funnel }: { funnel: AttributionGadsFunnel }) {
  const iosMax = Math.max(1, funnel.ios_total);
  const andMax = Math.max(1, funnel.android_total);
  const stepPct = (a: number, b: number) =>
    b > 0 ? `${Math.round((100 * a) / b)}%` : '';
  return (
    <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
      <div className='space-y-2'>
        <p className='text-sm font-semibold text-gray-900'>
          iOS (web fingerprint → offline upload)
        </p>
        <FunnelBar
          label='Attributions'
          value={funnel.ios_total}
          max={iosMax}
          color='#0ea5e9'
        />
        <FunnelBar
          label='With gclid'
          value={funnel.ios_with_gclid}
          max={iosMax}
          color='#0ea5e9'
          sub={stepPct(funnel.ios_with_gclid, funnel.ios_total)}
        />
        <FunnelBar
          label='gclid + paid'
          value={funnel.ios_gclid_paid}
          max={iosMax}
          color='#0284c7'
          sub={stepPct(funnel.ios_gclid_paid, funnel.ios_with_gclid)}
        />
        <FunnelBar
          label='Uploaded to Google'
          value={funnel.ios_gclid_paid_uploaded}
          max={iosMax}
          color='#10b981'
          sub={stepPct(
            funnel.ios_gclid_paid_uploaded,
            funnel.ios_gclid_paid,
          )}
        />
      </div>
      <div className='space-y-2'>
        <p className='text-sm font-semibold text-gray-900'>
          Android (install referrer → Firebase)
        </p>
        <FunnelBar
          label='Attributions'
          value={funnel.android_total}
          max={andMax}
          color='#8b5cf6'
        />
        <FunnelBar
          label='With gclid'
          value={funnel.android_with_gclid}
          max={andMax}
          color='#8b5cf6'
          sub={stepPct(funnel.android_with_gclid, funnel.android_total)}
        />
        <FunnelBar
          label='gclid + paid'
          value={funnel.android_gclid_paid}
          max={andMax}
          color='#7c3aed'
          sub={stepPct(funnel.android_gclid_paid, funnel.android_with_gclid)}
        />
        <p className='text-[11px] text-gray-400 pt-1'>
          Android conversions are reported to Google via Firebase, not the
          offline upload pipeline — so there is no “uploaded” step here.
        </p>
      </div>
    </div>
  );
}

function CampaignTable({ rows }: { rows: AttributionCampaignRow[] }) {
  return (
    <div className='border border-gray-200 rounded-md overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
          <tr>
            <th className='px-4 py-3 text-left'>Campaign</th>
            <th className='px-4 py-3 text-right'>Total</th>
            <th className='px-4 py-3 text-right'>iOS</th>
            <th className='px-4 py-3 text-right'>Android</th>
            <th className='px-4 py-3 text-right'>w/ gclid</th>
            <th className='px-4 py-3 text-right'>Paid</th>
            <th className='px-4 py-3 text-right'>Uploaded</th>
          </tr>
        </thead>
        <tbody className='divide-y divide-gray-100'>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={7} className='px-4 py-8 text-center text-gray-400'>
                No campaigns in this range.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.campaign} className='hover:bg-gray-50'>
                <td className='px-4 py-2 font-medium text-gray-800 max-w-[260px] truncate'>
                  {r.campaign}
                </td>
                <td className='px-4 py-2 text-right tabular-nums'>{r.total}</td>
                <td className='px-4 py-2 text-right tabular-nums text-gray-600'>
                  {r.ios}
                </td>
                <td className='px-4 py-2 text-right tabular-nums text-gray-600'>
                  {r.android}
                </td>
                <td className='px-4 py-2 text-right tabular-nums text-gray-600'>
                  {r.with_gclid}
                </td>
                <td className='px-4 py-2 text-right tabular-nums text-gray-700'>
                  {r.paid}
                </td>
                <td className='px-4 py-2 text-right tabular-nums text-gray-700'>
                  {r.uploaded}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function MainTab({ daysNum }: { daysNum: number }) {
  const { getToken } = useAuth();
  const query = useQuery({
    queryKey: ['admin', 'attribution-metrics', daysNum],
    queryFn: () =>
      apiJson<AttributionMonitorResponse>(
        getToken,
        `/v1/admin/attribution/metrics?days=${daysNum}`,
      ),
  });

  const data = query.data;
  const o = data?.overview;

  return (
    <div className='space-y-6'>
      {query.isLoading ? (
        <div className='flex items-center justify-center py-20'>
          <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
        </div>
      ) : query.isError ? (
        <div className='rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800'>
          Failed to load attribution metrics.
        </div>
      ) : data && o ? (
        <>
          {/* Overview */}
          <div className='grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6'>
            <StatCard
              label={`Attributions (${daysNum}d)`}
              value={o.total.toLocaleString()}
              hint={`${o.ios} iOS · ${o.android} Android`}
            />
            <StatCard
              label='With gclid'
              value={`${o.with_gclid}`}
              hint={`${pct(o.gclid_pct)} of attributions`}
            />
            <StatCard
              label='iOS match rate'
              value={pct(o.match_pct)}
              hint={`${o.fingerprints_matched}/${o.fingerprints_captured} fingerprints`}
            />
            <StatCard
              label='iOS upload coverage'
              value={pct(o.ios_upload_coverage_pct)}
              hint={`${o.ios_payers_uploaded}/${o.ios_payers_with_gclid} paid gclid users`}
              tone={o.ios_upload_coverage_pct >= 100 ? 'good' : 'warn'}
            />
            <StatCard
              label='Pending uploads'
              value={o.ios_pending_uploads.toLocaleString()}
              hint='paid iOS gclid, not uploaded'
              tone={o.ios_pending_uploads > 0 ? 'alert' : 'good'}
            />
            <StatCard
              label='Days since last upload'
              value={
                o.days_since_last_offline_upload == null
                  ? '—'
                  : o.days_since_last_offline_upload.toLocaleString()
              }
              hint={
                o.last_offline_upload_at
                  ? `last ${o.last_offline_upload_at}`
                  : 'no offline uploads yet'
              }
              tone={
                o.ios_pending_uploads > 0 &&
                (o.days_since_last_offline_upload == null ||
                  o.days_since_last_offline_upload >= 3)
                  ? 'alert'
                  : 'good'
              }
            />
          </div>
          <div className='grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6'>
            <StatCard
              label='False uploads'
              value={o.false_uploads_outstanding.toLocaleString()}
              hint='non-payers marked uploaded'
              tone={o.false_uploads_outstanding > 0 ? 'warn' : 'good'}
            />
          </div>

          {/* Data quality */}
          <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Data-quality flags
            </h2>
            <DataQualityPanel checks={data.data_quality} />
          </section>

          {/* Volume + match rate */}
          <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
            <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-2'>
              <h2 className='text-lg font-semibold text-gray-900'>
                Attribution volume per day
              </h2>
              <VolumeChart data={data.volume_by_day} />
            </section>
            <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-2'>
              <h2 className='text-lg font-semibold text-gray-900'>
                iOS fingerprint match rate (6 mo)
              </h2>
              <MatchRateTrend data={data.match_rate_trend} />
            </section>
          </div>

          {/* Google Ads funnel */}
          <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Google Ads conversion funnel ({daysNum}d)
            </h2>
            <GadsFunnel funnel={data.gads_funnel} />
          </section>

          {/* By source */}
          <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3'>
            <h2 className='text-lg font-semibold text-gray-900'>
              By source ({daysNum}d)
            </h2>
            <div className='border border-gray-200 rounded-md overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
                  <tr>
                    <th className='px-4 py-3 text-left'>Source</th>
                    <th className='px-4 py-3 text-left'>Platform</th>
                    <th className='px-4 py-3 text-right'>Count</th>
                    <th className='px-4 py-3 text-right'>w/ gclid</th>
                    <th className='px-4 py-3 text-right'>Paid</th>
                    <th className='px-4 py-3 text-right'>Uploaded</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {data.by_source.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className='px-4 py-8 text-center text-gray-400'
                      >
                        No attributions in this range.
                      </td>
                    </tr>
                  ) : (
                    data.by_source.map((r) => (
                      <tr key={r.source} className='hover:bg-gray-50'>
                        <td className='px-4 py-2 font-mono text-gray-800'>
                          {r.source}
                        </td>
                        <td className='px-4 py-2 text-gray-600'>{r.platform}</td>
                        <td className='px-4 py-2 text-right tabular-nums'>
                          {r.count}
                        </td>
                        <td className='px-4 py-2 text-right tabular-nums text-gray-600'>
                          {r.with_gclid}
                        </td>
                        <td className='px-4 py-2 text-right tabular-nums text-gray-700'>
                          {r.paid}
                        </td>
                        <td className='px-4 py-2 text-right tabular-nums text-gray-700'>
                          {r.uploaded}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* By campaign */}
          <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3'>
            <h2 className='text-lg font-semibold text-gray-900'>
              By campaign ({daysNum}d, top 15)
            </h2>
            <CampaignTable rows={data.by_campaign} />
          </section>

          {/* Recent */}
          <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-3'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Recent attributions
            </h2>
            <div className='border border-gray-200 rounded-md overflow-x-auto'>
              <table className='w-full text-sm'>
                <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
                  <tr>
                    <th className='px-4 py-3 text-left'>When</th>
                    <th className='px-4 py-3 text-right'>User</th>
                    <th className='px-4 py-3 text-left'>Platform</th>
                    <th className='px-4 py-3 text-left'>Source</th>
                    <th className='px-4 py-3 text-left'>Campaign</th>
                    <th className='px-4 py-3 text-left'>gclid</th>
                    <th className='px-4 py-3 text-center'>Paid</th>
                    <th className='px-4 py-3 text-center'>Uploaded</th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-gray-100'>
                  {data.recent.map((r) => (
                    <tr key={`${r.user_id}-${r.created_at}`} className='hover:bg-gray-50'>
                      <td className='px-4 py-2 font-mono text-xs text-gray-500 whitespace-nowrap'>
                        {r.created_at}
                      </td>
                      <td className='px-4 py-2 text-right tabular-nums text-gray-700'>
                        {r.user_id}
                      </td>
                      <td className='px-4 py-2 text-gray-700'>{r.platform}</td>
                      <td className='px-4 py-2 font-mono text-xs text-gray-600'>
                        {r.source}
                      </td>
                      <td className='px-4 py-2 text-gray-600 max-w-[180px] truncate'>
                        {r.campaign || '—'}
                      </td>
                      <td className='px-4 py-2 font-mono text-xs text-gray-500'>
                        {r.gclid || '—'}
                      </td>
                      <td className='px-4 py-2 text-center'>
                        {r.paid ? '✓' : ''}
                      </td>
                      <td className='px-4 py-2 text-center'>
                        {r.uploaded ? '✓' : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <p className='text-xs text-gray-400'>
            Integrity checks (flags, pipeline coverage) are all-time; volume,
            funnel, sources, and campaigns reflect the selected {daysNum}-day
            window.
          </p>
        </>
      ) : null}
    </div>
  );
}

function CampaignCard({ group }: { group: CampaignGroup }) {
  const [open, setOpen] = useState(false);
  return (
    <section className='bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden'>
      <button
        type='button'
        onClick={() => setOpen((v) => !v)}
        className='flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50'
      >
        <div className='flex items-center gap-2 min-w-0'>
          {open ? (
            <FiChevronDown className='h-4 w-4 shrink-0 text-gray-400' aria-hidden />
          ) : (
            <FiChevronRight className='h-4 w-4 shrink-0 text-gray-400' aria-hidden />
          )}
          <span className='truncate font-medium text-gray-900'>
            {group.campaign}
          </span>
        </div>
        <span className='shrink-0 rounded-full bg-harbor-50 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-primary'>
          {group.total.toLocaleString()}{' '}
          {group.total === 1 ? 'attribution' : 'attributions'}
        </span>
      </button>
      {open ? (
        <div className='border-t border-gray-100 overflow-x-auto'>
          <table className='w-full text-sm'>
            <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
              <tr>
                <th className='px-4 py-2 text-left'>When</th>
                <th className='px-4 py-2 text-right'>User</th>
                <th className='px-4 py-2 text-left'>Platform</th>
                <th className='px-4 py-2 text-left'>Source</th>
                <th className='px-4 py-2 text-left'>gclid</th>
                <th className='px-4 py-2 text-center'>Paid</th>
                <th className='px-4 py-2 text-center'>Uploaded</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-gray-100'>
              {group.attributions.map((r) => (
                <tr
                  key={`${r.user_id}-${r.created_at}`}
                  className='hover:bg-gray-50'
                >
                  <td className='px-4 py-2 font-mono text-xs text-gray-500 whitespace-nowrap'>
                    {r.created_at}
                  </td>
                  <td className='px-4 py-2 text-right tabular-nums text-gray-700'>
                    {r.user_id}
                  </td>
                  <td className='px-4 py-2 text-gray-700'>{r.platform}</td>
                  <td className='px-4 py-2 font-mono text-xs text-gray-600'>
                    {r.source}
                  </td>
                  <td className='px-4 py-2 font-mono text-xs text-gray-500'>
                    {r.gclid || '—'}
                  </td>
                  <td className='px-4 py-2 text-center'>{r.paid ? '✓' : ''}</td>
                  <td className='px-4 py-2 text-center'>
                    {r.uploaded ? '✓' : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function CampaignsTab({ daysNum }: { daysNum: number }) {
  const { getToken } = useAuth();
  const query = useQuery({
    queryKey: ['admin', 'attribution-campaigns', daysNum],
    queryFn: () =>
      apiJson<AttributionCampaignsResponse>(
        getToken,
        `/v1/admin/attribution/campaigns?days=${daysNum}`,
      ),
  });

  if (query.isLoading) {
    return (
      <div className='flex items-center justify-center py-20'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className='rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800'>
        Failed to load campaign attributions.
      </div>
    );
  }

  const campaigns = query.data?.campaigns ?? [];
  if (campaigns.length === 0) {
    return (
      <div className='rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-400'>
        No attributions in this range.
      </div>
    );
  }

  return (
    <div className='space-y-3'>
      <p className='text-sm text-gray-500'>
        {campaigns.length} {campaigns.length === 1 ? 'campaign' : 'campaigns'} in
        the last {daysNum} days. Click a campaign to see its attributions, newest
        first.
      </p>
      {campaigns.map((g) => (
        <CampaignCard key={g.campaign} group={g} />
      ))}
    </div>
  );
}

function AdminAttributionPage() {
  const navigate = useNavigate({ from: '/admin/attribution' });
  const { days, tab } = Route.useSearch();
  const daysNum = Number(days);
  const queryClient = useQueryClient();

  const activeKey =
    tab === 'campaigns'
      ? ['admin', 'attribution-campaigns', daysNum]
      : ['admin', 'attribution-metrics', daysNum];
  const isFetching = useIsFetching({ queryKey: activeKey }) > 0;

  const onChangeRange = (next: RangeDays) => {
    if (next === days) return;
    navigate({ search: (prev) => ({ ...prev, days: next }) });
  };

  const onChangeTab = (next: SubTab) => {
    if (next === tab) return;
    navigate({ search: (prev) => ({ ...prev, tab: next }) });
  };

  const subTab = (value: SubTab, label: string) => {
    const active = value === tab;
    return (
      <button
        key={value}
        type='button'
        onClick={() => onChangeTab(value)}
        className={`px-3 py-2 text-sm border-b-2 -mb-px ${
          active
            ? 'border-primary text-primary'
            : 'border-transparent text-gray-500 hover:text-gray-700'
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className='space-y-6'>
      <div className='flex flex-wrap items-end justify-between gap-3'>
        <div>
          <h1 className='text-2xl font-semibold text-gray-900'>Attribution</h1>
          <p className='text-sm text-gray-500 mt-1'>
            iOS vs Android attribution health, fingerprint match rate, and the
            Google Ads conversion-upload pipeline.
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <div className='inline-flex rounded-md border border-gray-200 bg-white p-0.5 shadow-sm'>
            {RANGE_OPTIONS.map((opt) => {
              const active = opt.days === days;
              return (
                <button
                  key={opt.days}
                  type='button'
                  onClick={() => onChangeRange(opt.days)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded ${
                    active
                      ? 'bg-harbor-50 text-primary'
                      : 'text-gray-500 hover:text-gray-800'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <button
            type='button'
            onClick={() => queryClient.invalidateQueries({ queryKey: activeKey })}
            className='inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 shadow-sm hover:text-gray-900'
          >
            <FiRefreshCw
              className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`}
              aria-hidden
            />
            Refresh
          </button>
        </div>
      </div>

      <nav className='flex flex-wrap gap-2 border-b border-gray-200 pb-px'>
        {subTab('main', 'Main')}
        {subTab('campaigns', 'Campaigns')}
      </nav>

      {tab === 'campaigns' ? (
        <CampaignsTab daysNum={daysNum} />
      ) : (
        <MainTab daysNum={daysNum} />
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/attribution')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { days: RangeDays; tab: SubTab } => {
    const rawDays = String(search.days ?? '');
    const rawTab = String(search.tab ?? '');
    return {
      days: ALLOWED_DAYS.has(rawDays) ? (rawDays as RangeDays) : '30',
      tab: ALLOWED_TABS.has(rawTab) ? (rawTab as SubTab) : 'main',
    };
  },
  component: AdminAttributionPage,
});
